import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ManuscriptEditor = ({ chapters, onChapterUpdate, onRegenerateChapter }) => {
  const [selectedChapter, setSelectedChapter] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleEditStart = () => {
    setEditedContent(chapters?.[selectedChapter]?.content);
    setIsEditing(true);
  };

  const handleSave = () => {
    onChapterUpdate(selectedChapter, editedContent);
    setIsEditing(false);
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    await onRegenerateChapter(selectedChapter);
    setIsRegenerating(false);
  };

  const currentChapter = chapters?.[selectedChapter];
  const wordCount = currentChapter?.content?.split(/\s+/)?.length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b border-border bg-muted/30">
            <h3 className="text-sm font-heading font-semibold text-foreground">
              Chapters
            </h3>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {chapters?.map((chapter, index) => (
              <button
                key={chapter?.id}
                onClick={() => setSelectedChapter(index)}
                className={`w-full p-4 text-left border-b border-border transition-colors duration-200 ${
                  selectedChapter === index
                    ? 'bg-primary/10 border-l-4 border-l-primary' :'hover:bg-muted/50'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className={`text-sm font-medium ${
                    selectedChapter === index ? 'text-primary' : 'text-foreground'
                  }`}>
                    {chapter?.title}
                  </span>
                  {chapter?.isComplete && (
                    <Icon name="CheckCircle" size={14} className="text-success flex-shrink-0" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{chapter?.wordCount?.toLocaleString()} words</span>
                  {chapter?.lastEdited && (
                    <>
                      <span>•</span>
                      <span>{chapter?.lastEdited}</span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:col-span-3">
        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground mb-1">
                {currentChapter?.title}
              </h2>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Icon name="FileText" size={14} />
                  {wordCount?.toLocaleString()} words
                </span>
                <span className="flex items-center gap-1">
                  <Icon name="Clock" size={14} />
                  {currentChapter?.lastEdited || 'Never edited'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!isEditing ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit2"
                    iconPosition="left"
                    onClick={handleEditStart}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    loading={isRegenerating}
                    onClick={handleRegenerate}
                  >
                    Regenerate
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Check"
                    iconPosition="left"
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="p-6">
            {isEditing ? (
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e?.target?.value)}
                className="w-full min-h-[500px] p-4 bg-background border border-border rounded-md text-foreground font-body text-base leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary resize-y"
                placeholder="Start writing your chapter content..."
              />
            ) : (
              <div className="prose prose-slate max-w-none">
                <div className="text-foreground font-body text-base leading-relaxed whitespace-pre-wrap">
                  {currentChapter?.content}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            iconPosition="left"
            disabled={selectedChapter === 0}
            onClick={() => setSelectedChapter(selectedChapter - 1)}
          >
            Previous Chapter
          </Button>

          <span className="text-sm text-muted-foreground">
            Chapter {selectedChapter + 1} of {chapters?.length}
          </span>

          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            iconPosition="right"
            disabled={selectedChapter === chapters?.length - 1}
            onClick={() => setSelectedChapter(selectedChapter + 1)}
          >
            Next Chapter
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManuscriptEditor;